import { AxiosError, AxiosResponse } from 'axios';
import { Observable, catchError, from, map, tap } from 'rxjs';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';


import { TableFilter } from '@store/common/tableFilter.model';
import { AuditOfferFollowUpFilters, AuditOfferFollowUpPagination, AuditOfferFollowUpResponseDto, AuditOfferLightDto, AuditProposalResponseDto, CreateAuditOfferFollowUp, ProposalSupplierActivities } from './audit-offer-followUp.model';
import { getAuditOfferFollowUpDataSource, getAuditsOfferActivitiesDataSource, getAuditsOfferFromProposalDataSource, getAuditsProposalDetailsDataSource } from './audit-offer-followUp.requests';
import { auditOfferFollowUpStore } from './audit-offer-followUp.store';

import { Pagination } from '@store/common/pagination.model';

import { GridSortItem } from '@mui/x-data-grid-pro';
import { upsertEntities } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';

export class AuditsFollowUpService {
  store = auditOfferFollowUpStore;

  resetStore = () => this.store.reset();

  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: { search: '', tableFilters: AuditOfferFollowUpFilters.initTableFilters() },
  }));

  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getAuditOfferFollowUp = (auditId: string | number, isRecentProposal: boolean): Observable<AuditOfferFollowUpPagination> => {
    const { filters: { search, tableFilters }, sort, tablePagination } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETAuditsProposal(auditId),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        isRecentProposal: isRecentProposal,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditOfferFollowUpPagination>) => {
          return response.data;
        }),
        tap((auditFollowUpPagination) => {
          const paginationData = Pagination.toPaginationData(auditFollowUpPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(auditFollowUpPagination.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, auditFollowUpPagination.data.map((auditFollowUp) => auditFollowUp.id)),
            getAuditOfferFollowUpDataSource.setSuccess(),
          );
        }),
        getAuditOfferFollowUpDataSource.trackRequestStatus(),
      );
  };


  createAuditProposal = ( auditId: number | string, data: CreateAuditOfferFollowUp) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditOfferFollowUp(auditId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditOfferFollowUpResponseDto>) => {
          return response.data;
        }),
        tap((proposalResponse) => {
            this.store.update((state) => ({
                ...state,
                proposalResponse,
            }));
        }),
        );
  };


  getAuditsOfferActivities = (auditId: string | number): Observable<ProposalSupplierActivities[]> => {
   return from(APIAxios({
      ...APIRoutes.GETAuditsOfferActivities(auditId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<ProposalSupplierActivities[]>) => {
            return response.data;
          }),
          tap((activities) => {
              this.store.update((state) => ({
                  ...state,
                  activities,
              }));
              getAuditsOfferActivitiesDataSource.setSuccess();
          }),
          getAuditsOfferActivitiesDataSource.trackRequestStatus(),
        );
  };

    getAuditsProposalDetails = (auditProposalId: string | number): Observable<AuditProposalResponseDto> => {
   return from(APIAxios({
      ...APIRoutes.GETAuditsProposalDetails(auditProposalId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<AuditProposalResponseDto>) => {
            return response.data;
          }),
          tap((auditsProposalDetails) => {
              this.store.update((state) => ({
                  ...state,
                   auditsProposalDetails,
              }));
              getAuditsProposalDetailsDataSource.setSuccess();
          }),
          getAuditsProposalDetailsDataSource.trackRequestStatus(),
        );
  };

  getAuditsOfferFromProposal = (auditProposalId: string | number): Observable<AuditOfferLightDto[]> => {
   return from(APIAxios({
      ...APIRoutes.GETAuditsOfferFromProposal(auditProposalId),
    }))
      .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<AuditOfferLightDto[]>) => {
            return response.data;
          }),
          tap((allAuditsOfferFromProposal) => {
              this.store.update((state) => ({
                  ...state,
                   allAuditsOfferFromProposal,
              }));
              getAuditsOfferFromProposalDataSource.setSuccess();
          }),
          getAuditsOfferFromProposalDataSource.trackRequestStatus(),
        );
  };

}

export const auditsFollowUpService = new AuditsFollowUpService();
